'use memo';

import { createFileRoute, useParams } from '@tanstack/react-router';
import { StatCard, StatCardContent, StatCardPrimaryValue, StatCardSecondaryValue, StatCardTable, StatCardTableRow, StatCardTitle, StatCardValueContainer } from '@/components/Card/StatCard';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetReleaseTestOverview } from '@/features/stacktrace/release-tests/-query';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { stacktraceNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_stacktrace/test-suites/$id/_layout/overview/')({
  component: TestSuiteOverviewPage
});
function TestSuiteOverviewPage() {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'testSuites.detail.tabs'
  });
  const {
    t: tOverview
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'testSuites.detail.overview'
  });
  useDocumentTitle(t('overview'));
  const {
    id
  } = useParams({
    from: '/_stacktrace/test-suites/$id/_layout'
  });
  const {
    data,
    isPending
  } = useGetReleaseTestOverview(id);
  if (isPending || !data) {
    return <VStack className="grow gap-4 px-12 py-5">
        <Text variant="h2">{t('overview')}</Text>
        <HStack className="gap-4 items-stretch flex-wrap">
          {[0, 1, 2, 3].map(i => <div key={i} className="flex-1 min-w-[240px]">
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>)}
        </HStack>
      </VStack>;
  }
  return <VStack className="grow gap-4 px-12 py-5">
      <Text variant="h2">{t('overview')}</Text>
      <HStack className="gap-4 items-stretch flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <StatCard className="w-full">
            <StatCardTitle>{tOverview('testProgress.title')}</StatCardTitle>
            <StatCardContent>
              <StatCardValueContainer>
                <StatCardPrimaryValue>
                  {data.testProgress.percentage}
                </StatCardPrimaryValue>
                <StatCardSecondaryValue>
                  {tOverview('units.percent')}
                </StatCardSecondaryValue>
              </StatCardValueContainer>
              <Text variant="labelSmall" className="mt-2 text-neutral-muted-on-fill">
                {tOverview('testProgress.caseCompleted', {
                completed: data.testProgress.completedCount,
                total: data.testProgress.totalCount
              })}
              </Text>
            </StatCardContent>
          </StatCard>
        </div>
        <div className="flex-1 min-w-[240px]">
          <StatCard className="w-full">
            <StatCardTitle>
              {tOverview('progressDelayRate.title')}
            </StatCardTitle>
            <StatCardContent>
              <StatCardValueContainer>
                <StatCardPrimaryValue>
                  {data.progressDelayRate.percentage}
                </StatCardPrimaryValue>
                <StatCardSecondaryValue>
                  {tOverview('units.percent')}
                </StatCardSecondaryValue>
              </StatCardValueContainer>
              <Text variant="labelSmall" className="mt-2 text-neutral-muted-on-fill">
                {tOverview('progressDelayRate.dayOverDay', {
                delta: data.progressDelayRate.dayOverDayDelta
              })}
              </Text>
            </StatCardContent>
          </StatCard>
        </div>
        <div className="flex-1 min-w-[240px]">
          <StatCard className="w-full">
            <StatCardTitle>{tOverview('blockingIssues.title')}</StatCardTitle>
            <StatCardContent>
              <StatCardValueContainer>
                <StatCardPrimaryValue>
                  {data.blockingIssues.count}
                </StatCardPrimaryValue>
                <StatCardSecondaryValue>
                  {tOverview('units.count')}
                </StatCardSecondaryValue>
              </StatCardValueContainer>
              <Text variant="labelSmall" className="mt-2 text-neutral-muted-on-fill">
                {tOverview('blockingIssues.unresolvedThreshold', {
                hours: data.blockingIssues.maxUnresolvedHours
              })}
              </Text>
            </StatCardContent>
          </StatCard>
        </div>
        <div className="flex-1 min-w-[240px]">
          <StatCard className="w-full">
            <StatCardTitle>
              {tOverview('releaseConcernIssues.title')}
            </StatCardTitle>
            <StatCardContent>
              <StatCardValueContainer>
                <StatCardPrimaryValue>
                  {data.releaseConcernIssues.count}
                </StatCardPrimaryValue>
                <StatCardSecondaryValue>
                  {tOverview('units.count')}
                </StatCardSecondaryValue>
              </StatCardValueContainer>
              <Text variant="labelSmall" className="mt-2 text-neutral-muted-on-fill">
                {tOverview('releaseConcernIssues.unresolvedThreshold', {
                hours: data.releaseConcernIssues.maxUnresolvedHours
              })}
              </Text>
            </StatCardContent>
          </StatCard>
        </div>
      </HStack>
      <HStack className="gap-4 items-stretch flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <FixModulesCard data={data.fixModules} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <IssueSeverityCard data={data.issueSeverityDistribution} />
        </div>
      </HStack>
    </VStack>;
}
function FixModulesCard({
  data
}: {
  data: {
    total: number;
    moduleCount: number;
    subModuleCount: number;
    functionCount: number;
  };
}) {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'testSuites.detail.overview'
  });
  const rows = [{
    label: t('fixModules.module'),
    value: data.moduleCount
  }, {
    label: t('fixModules.subModule'),
    value: data.subModuleCount
  }, {
    label: t('fixModules.function'),
    value: data.functionCount
  }];
  return <StatCard className="w-full h-full">
      <StatCardTitle>{t('fixModules.title')}</StatCardTitle>
      <StatCardContent>
        <StatCardValueContainer>
          <StatCardPrimaryValue>{data.total}</StatCardPrimaryValue>
          <StatCardSecondaryValue>{t('units.count')}</StatCardSecondaryValue>
        </StatCardValueContainer>
        <div className="my-3 h-px bg-neutral-muted-border" />
        <StatCardTable>
          {rows.map(row => <StatCardTableRow key={row.label}>
              <Text variant="label" className="text-neutral-on-fill whitespace-nowrap">
                {row.label}
              </Text>
              <span />
              <Text variant="label" className="font-mono text-neutral-on-fill">
                {row.value}
              </Text>
            </StatCardTableRow>)}
        </StatCardTable>
      </StatCardContent>
    </StatCard>;
}
function IssueSeverityCard({
  data
}: {
  data: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    enhanced: number;
    total: number;
  };
}) {
  const {
    t
  } = useTranslation(stacktraceNS, {
    keyPrefix: 'testSuites.detail.overview'
  });
  const rows = [{
    key: 'critical' as const,
    value: data.critical
  }, {
    key: 'major' as const,
    value: data.high
  }, {
    key: 'normal' as const,
    value: data.medium
  }, {
    key: 'minor' as const,
    value: data.low
  }, {
    key: 'enhancement' as const,
    value: data.enhanced
  }];
  const max = Math.max(...rows.map(row => row.value), 1);
  return <StatCard className="w-full h-full">
      <StatCardTitle>{t('issueSeverity.title')}</StatCardTitle>
      <StatCardContent>
        <StatCardValueContainer>
          <StatCardPrimaryValue>{data.total}</StatCardPrimaryValue>
          <StatCardSecondaryValue>{t('units.count')}</StatCardSecondaryValue>
        </StatCardValueContainer>
        <div className="my-3 h-px bg-neutral-muted-border" />
        <StatCardTable>
          {rows.map(row => <StatCardTableRow key={row.key}>
              <Text variant="label" className="text-neutral-on-fill whitespace-nowrap">
                {t(`issueSeverity.${row.key}` as const)}
              </Text>
              <div className="w-full px-2">
                <div className="h-2 rounded-full bg-primary-muted-fill" style={{
              width: `${row.value / max * 100}%`
            }} />
              </div>
              <Text variant="label" className="font-mono text-neutral-on-fill">
                {row.value}
              </Text>
            </StatCardTableRow>)}
        </StatCardTable>
      </StatCardContent>
    </StatCard>;
}